<template>
    <p-feature-flag
    name="Accounting"
    redirect>
        <div>
            <p-search-screen
                title="General Ledger"
                api-destination="generalledgers"
                search-destination="searchheaders"
                :filters="filters"
                :fields="fields"
                :filterBy="filterBy"
                use-bootstrap-selection
                add-new-button
                ref="searchScreen"
                @total-rows-changed="e => (totalRows = e)">
                 <template v-slot:actionButtons>
                    <b-row class="pl-2">
                        <b-col class="ps-0">
                            <b-button
                                variant="outline-primary"
                                v-on:click="printScreen()"
                                :disabled="totalRows <= 0 ">
                                Print
                            </b-button>
                            <b-button 
                                class="ms-2"    
                                variant="outline-primary" 
                                @click="downloadFile('csv')" 
                                :disabled="totalRows <= 0 ">
                                Export (CSV)
                            </b-button>
                            <b-button
                                class="ms-2"    
                                variant="outline-primary"
                                :to="{ path: '/generalledgerdetails' }">
                                General Ledger Raw
                            </b-button>
                        </b-col>
                    </b-row>
                </template>
                <template v-slot:belowSearch>
                    <p-form>
                        <b-row class="mt-4">
                            <b-col cols="2" sm="4" class="me-2 ml-4">
                                <p-datepicker label="Start Activity Date"
                                    v-model="dateRange.startDate"
                                    vid="activityStartDate" />
                            </b-col>
                            <b-col cols="2" sm="4" class="me-2">
                                <p-datepicker label="End Activity Date"
                                    vid="endDate"
                                    rules="dateGreaterOrEqual:@activityStartDate"
                                    v-model="dateRange.endDate" />
                            </b-col>
                        </b-row>
                    </p-form>
                </template>
                <template v-slot:cell(view)="{ item }">
                    <p-button
                        variant="outline-primary"
                        size="sm"
                        style="padding-top: 1px; padding-bottom: 1px;"
                        @click="viewDetails(item)">
                        View
                    </p-button>
                </template>
            </p-search-screen>

            <p-modal
                name="glDetailModal"
                label="General Ledger Details"
                size="xl"
                dialog-class="modal-90w"
                ok-only
                ref="glDetailModal">
                <template v-slot:header-center>
                    <h4>Description</h4><h3 class="mb-0">{{ selectedLedger?.description }}</h3>
                </template>
                <template v-slot:default>
                    <div v-if="selectedLedger">
                        <b-row class="align-items-center mb-3">
                            <b-col>
                                <p-input
                                    label="Journal Number"
                                    :model-value="selectedLedger.transactionNumber"
                                    disabled />
                            </b-col>
                            <b-col>
                                <p-input
                                    label="Accounting Date"
                                    :model-value="selectedLedger.accountingDate"
                                    disabled />
                            </b-col>
                            <b-col>
                                <p-input
                                    label="Activity Date"
                                    :model-value="formatDateOnly(selectedLedger.activityDate)"
                                    disabled />
                            </b-col>
                            <b-col>
                                <p-input
                                    label="Post Date"
                                    :model-value="formatDate(selectedLedger.postDate)"
                                    disabled />
                            </b-col>
                            <b-col>
                                <p-input
                                    label="Posted"
                                    :model-value="selectedLedger.isPosted ? 'Yes' : 'No'"
                                    disabled />
                            </b-col>
                        </b-row>

                        <b-card no-body>
                            <b-card-body class="p-0">
                                <p-table
                                    :fields="detailFields"
                                    :items="ledgerDetails"
                                    :enable-row-selection="false"
                                    :per-page="5000"
                                    :show-record-count="false"
                                    :is-busy="isDetailBusy" />
                            </b-card-body>
                        </b-card>
                    </div>
                </template>
            </p-modal>
        </div>
    </p-feature-flag>
</template>

<script>
import { datetimeToLocalTimeFormatter, currencyFormatter, dateOnlyFormatter } from '../../components/Common/Formatters.js';
import { downloadFileToBrowser } from '@/components/Common/BrowserDownload.js';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import SearchStorage from '@/components/mixins/SearchFilterStorage.js';
import DateOnlyPickerFilters from '@/components/mixins/DateOnlyPickerFilters.js';
import axios from 'axios';

export default {
    // DateOnlyPickerFilters must follow SearchStorage — it seeds from the filters that mixin restores.
    mixins: [SearchStorage, DateOnlyPickerFilters],
    props: {
        customers: Object,
        account: Object
    },
    methods: {
    printScreen(){
            window.print();
        },
        dccFormatter(value) {
            return value.padStart(6, '0'); // Fixes DCCs like 030101 which would otherwise be displayed as 30101
        },        
        async downloadFile(exportFormat) {
            let apiUrl = 'GeneralLedgers/csv';
            this.isBusy = true;
            axios.post(
                    apiUrl,
                    this.filters,
                    { responseType: 'blob' })
                .then(result => {
                    downloadFileToBrowser(
                        result.data,
                        exportFormat == 'pdf' ? `General Ledger` : `General Ledger.csv`,
                        exportFormat == 'pdf' ? 'application/pdf' : 'application/csv');

                        this.$appSuccess(`General Ledger report generated successfully.`)
                })
                .finally(() => {
                    this.isBusy = false;
            });
        },
        formatDate(value) {
            return datetimeToLocalTimeFormatter(value);
        },
        formatDateOnly(value) {
            return dateOnlyFormatter(value);
        },
        async viewDetails(item) {
            this.selectedLedger = item;
            this.ledgerDetails = [];
            this.isDetailBusy = true;
            this.$refs.glDetailModal.show();
            try {
                const resp = await axios.get('generalledgers/detail', {
                    params: { generalLedgerId: item.generalLedgerId }
                });
                this.ledgerDetails = resp.data;
            } finally {
                this.isDetailBusy = false;
            }
        }
    },
    data() {
        return {
            filterName: 'GeneralLedger',
            selectedCustomers: [],
            isBusy: false,
            totalRows: 0,
            selectedLedger: null,
            ledgerDetails: [],
            isDetailBusy: false,
            // Datepicker-bound models hold the store-tz ISO the picker emits; DateOnlyPickerFilters
            // translates between these and the YYYY-MM-DD filters below. Neither picker carries
            // is-end-date, so neither pairing sets it.
            dateRange: {
                startDate: null,
                endDate: null
            },
            datePickerFilters: [
                { model: 'dateRange.startDate', filter: 'startDate' },
                { model: 'dateRange.endDate', filter: 'endDate' }
            ],
            filters: {
                automatic: '',
                searchBy: 'All',
                perPage: 50,
                sortBy: 'createDate',
                sortDesc: true,
                startDate: null,
                endDate: null,
                locationId: null,
            },
            filterBy: ['All','Journal Number', 'Description'],
            fields: [
                {
                    key: 'view',
                    label: 'Details',
                    tdClass: 'text-left pl-2'
                },
                {
                    key: 'transactionNumber',
                    label: 'Journal Number',
                    sortable: true,
                },
                {
                    key: 'accountingDate',
                    label: 'Accounting Date',
                    sortable: true,
                },
                {
                    label: 'Activity Date',
                    key: 'activityDate',
                    formatter: dateOnlyFormatter,
                    sortable: true,
                    sortByformatted: true
                },
                {
                    label: 'Post Date',
                    key: 'postDate',
                    formatter: datetimeToLocalTimeFormatter,
                    sortable: true,
                    sortByformatted: true
                },
                {
                    key: 'description',
                    label: 'Description',
                    sortable: true
                },
                {
                    key: 'isPosted',
                    label: 'Posted',
                    sortable: true,
                    formatter: value => value ? 'Yes' : 'No'
                },
                {
                    label: 'Entry Date',
                    key: 'createDate',
                    formatter: datetimeToLocalTimeFormatter,
                    sortable: true,
                    sortByformatted: true
                },                
            ],
            detailFields: [
                {
                    key: 'locationName',
                    label: 'Location',
                    sortable: true
                },
                {
                    key: 'glCode',
                    label: 'GL Code',
                    sortable: true
                },
                {
                    key: 'glCodeDescription',
                    label: 'GL Description',
                    sortable: true
                },
                {
                    key: 'entryType',
                    label: 'Entry Type',
                    sortable: true
                },
                {
                    key: 'entryReference',
                    label: 'Reference',
                    sortable: true
                },
                {
                    key: 'userName',
                    label: 'User',
                    sortable: true
                },
                {
                    key: 'debit',
                    label: 'Debit',
                    sortable: true,
                    formatter: value => currencyFormatter(value),
                    tdClass: 'text-right',
                    thClass: 'text-right'
                },
                {
                    key: 'credit',
                    label: 'Credit',
                    sortable: true,
                    formatter: value => currencyFormatter(value),
                    tdClass: 'text-right',
                    thClass: 'text-right'
                },
                {
                    key: 'createDate',
                    label: 'Created',
                    sortable: true,
                    formatter: datetimeToLocalTimeFormatter
                }
            ]
        };
    },
    mounted: async function() {
        this.filters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
    }
};
</script>

<style>
.modal-90w {
    max-width: 75vw;
}

.modal-90w .p-input {
    margin-bottom: 0;
}
</style>
